import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_RotateImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/rotate-image',
			name: 'Rotate Image online — Rotate Photo by Degrees | Lift',
			description:
				'Rotate image online in seconds. No watermark. Upload photo, rotate picture left or right 90 degrees, fix a picture sideways, and download perfectly aligned images instantly. Try now!',
			url: 'https://liftapp.ai/rotate-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/rotate-image#product' },
				{ '@id': 'https://liftapp.ai/rotate-image#howto' },
				{ '@id': 'https://liftapp.ai/rotate-image#benefits' },
				{ '@id': 'https://liftapp.ai/rotate-image#faq' },
				{ '@id': 'https://liftapp.ai/rotate-image#why-lift' },
				{ '@id': 'https://liftapp.ai/rotate-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/rotate-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/rotate-image#product',
			name: 'Image Rotator Online',
			description:
				'Rotate photo online and fix any picture sideways instantly. Use our rotate image online tool to rotate picture, spin image 90 degrees left or right, and get perfectly aligned results, no watermarks.',
			url: 'https://liftapp.ai/rotate-image',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/rotate-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Rotate Your Image',
					text: 'Use the rotate controls above your photo to rotate it instantly, or click Left or Right to turn it exactly 90 degrees.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/rotate-image#benefits',
			name: 'Who can benefit from Online Image Rotator?',
			description:
				'Lift Image Rotator helps everyday users, social media creators, students, e-commerce sellers, and digital marketers rotate and fix picture orientation online.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Everyday Smartphone Users — Fix sideways or upside-down pictures in seconds without installing any app.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Social Media Users — Keep your feed looking sharp with correctly oriented posts, stories, and reels.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Students & Office Workers — Fix rotated scanned documents, IDs, and assignments before submission.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'E-commerce Sellers — Rotate logo, product shots, or pictures that uploaded incorrectly.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Digital Marketers — Speed up your content publishing workflow by fixing image orientation instantly.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Lift Online Image Rotator for Any Need',
				description: 'Lift Online Image Rotator for Any Need',
			},
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/rotate-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Image Rotator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Rotate Image Online is an easy-to-use tool that lets you rotate image online in just a few clicks. It helps fix a picture sideways, correct a rotated picture, and quickly spin image files 90 degrees for the perfect orientation.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Image Rotator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Simply upload your photo from your device, then use the built-in controls to rotate the image online. Tap Left or Right to rotate photo exactly 90 degrees, fix image turn issues, and instantly download your properly oriented result.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe — do you store my images?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes, completely safe. Your images are processed securely and automatically deleted from our servers immediately after compression. We never store, share, or access your files — your privacy is our priority.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/rotate-image#why-lift',
			name: 'Why Lift Rotate Image',
			description:
				'High-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Spin, rotate and fix picture sideways with Lift online tool',
				description:
					'Spin, rotate and fix picture sideways with Lift online tool',
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/rotate-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/rotate-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Rotate Image',
					item: 'https://liftapp.ai/rotate-image',
				},
			],
		},
	],
};
